import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Permission check for prepaid_follows_radio visibility/editability.
 * Modify this function to change who can see/edit the prepaid follows radio flag.
 */
export const canViewPrepaidFollowsRadio = user => {
  return user?.get('is_staff', false) || user?.get('is_superuser', false);
};

export const canEditPrepaidFollowsRadio = user => {
  return user?.get('is_staff', false) || user?.get('is_superuser', false);
};

function RadioChangeConfirmationDialog({
  open,
  onClose,
  onConfirm,
  currentRadio,
  newRadio,
  radioAction,
  setRadioAction,
}) {
  const { t } = useTranslation();

  const isDeleting = newRadio == null;

  const handleConfirm = () => {
    onConfirm(radioAction);
  };

  const handleRadioActionChange = event => {
    setRadioAction(event.target.value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('sites.confirmRadioChange', 'Confirm Radio Change')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            {t('sites.radioChangeConfirmationMessage', 'You are about to change the radio assigned to this unit.')}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('sites.currentRadio', 'Current Radio')}
          </Typography>
          <Box sx={{ pl: 2, py: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{t('sites.iccidMac', 'ICCID/MAC')}:</strong>{' '}
              {currentRadio?.iccid || currentRadio?.mac_address || t('general.unknown', 'Unknown')}
            </Typography>
            {currentRadio?.ip_address && (
              <Typography variant="body2">
                <strong>{t('sites.ipAddress', 'IP Address')}:</strong> {currentRadio.ip_address}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('sites.newRadio', 'New Radio')}
          </Typography>
          {isDeleting ? (
            <Box sx={{ pl: 2, py: 1, backgroundColor: 'primary.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('general.none', 'None')}</strong>
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pl: 2, py: 1, backgroundColor: 'primary.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('sites.iccidMac', 'ICCID/MAC')}:</strong>{' '}
                {newRadio?.iccid || newRadio?.mac_address || t('general.unknown', 'Unknown')}
              </Typography>
              {newRadio?.ip_address && (
                <Typography variant="body2">
                  <strong>{t('sites.ipAddress', 'IP Address')}:</strong> {newRadio.ip_address}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">
            {t('sites.whatShouldHappenToExistingUnit', 'What should happen to the existing radio?')}
          </FormLabel>
          <RadioGroup name="radio_action" value={radioAction} onChange={handleRadioActionChange} sx={{ mt: 1 }}>
            <FormControlLabel
              value="inventory"
              control={<Radio />}
              label={
                <div>
                  <Typography variant="body2" component="span" fontWeight="bold">
                    {t('sites.returnToInventory')}
                  </Typography>
                  <Typography variant="body2" component="div" color="text.secondary">
                    <span dangerouslySetInnerHTML={{ __html: t('sites.returnToInventoryDescription') }} />
                  </Typography>
                </div>
              }
            />
            <FormControlLabel
              value="purge"
              control={<Radio />}
              label={
                <div>
                  <Typography variant="body2" component="span" fontWeight="bold">
                    {t('sites.retire')}
                  </Typography>
                  <Typography variant="body2" component="div" color="text.secondary">
                    <span dangerouslySetInnerHTML={{ __html: t('sites.retireDescription') }} />
                  </Typography>
                </div>
              }
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={!radioAction}>
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RadioChangeConfirmationDialog;
